import { ScrollToTop, SnackbarMessage, TelemetryProvider } from "@knowfully/ui.common";
import { AppDependenciesProvider } from "globalProviders/AppDependenciesProvider";
import { useAuthContext } from "globalProviders/AuthProvider";
import useGlobalContext from "globalProviders/GlobalDataProvider";
import useBaseCss from "hooks/useBaseCss";
import { useTelemetryConfig } from "hooks/useTelemetryConfig";
import LeftMenuLayout from "layouts/LeftMenuLayout";
import AgreementsReviewPage from "pages/AgreementsReviewPage";
import CourseDetailsPage from "pages/CourseDetailsPage";
import CoursesPage from "pages/CoursesPage";
import ErrorPage from "pages/ErrorPage";
import DocumentsPage from "pages/DocumentsPage";
import LoginComplete from "pages/LoginCompletePage";
import NotFoundPage from "pages/NotFoundPage";
import SummaryPage from "pages/SummaryPage";
import UnauthorizedPage from "pages/UnauthorizedPage";
import { FC } from "react";
import ReactDOM from "react-dom/client";
import { QueryClient } from "react-query";
import {
  BrowserRouter,
  Outlet,
  Route,
  RouteProps,
  Routes,
} from "react-router-dom";

const baseUrl = document
  .getElementsByTagName("base")[0]
  .getAttribute("href") as string | undefined;

const rootElement = document.getElementById("root")!;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      retryOnMount: false,
      refetchOnWindowFocus: false,
    },
  },
});

const LoggedInRequired: FC<RouteProps> = ({ children }) => {
  const { isAuthenticated } = useAuthContext();

  if (!isAuthenticated) {
    return <UnauthorizedPage />;
  }

  return (
    <LeftMenuLayout>
      <Outlet />
    </LeftMenuLayout>
  );
};

const AppRoutes = () => {
  useBaseCss();

  const {
    summaryUrl,
    coursesUrl,
    courseDetailsUrl,
    documentsUrl,
    agreementReviewUrl,
    loginCompleteUrl,
    errorUrl,
    notFoundUrl,
  } = useGlobalContext();

  return (
    <Routes>
      <Route path={loginCompleteUrl()} element={<LoginComplete />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      <Route path="" element={<LoggedInRequired />}>
        <Route path={summaryUrl()} element={<SummaryPage />} />
        <Route path={coursesUrl()} element={<CoursesPage />} />
        <Route path={courseDetailsUrl()} element={<CourseDetailsPage />} />
        <Route path={documentsUrl()} element={<DocumentsPage />} />
        <Route path={agreementReviewUrl()} element={<AgreementsReviewPage />} />
        <Route path={errorUrl()} element={<ErrorPage />} />
        <Route path={notFoundUrl()} element={<NotFoundPage />} />
        <Route path={"*"} element={<NotFoundPage />} />
      </Route>

      <Route path={notFoundUrl()} element={<NotFoundPage />} />
      <Route path={"*"} element={<NotFoundPage />} />
    </Routes>
  );
};

const AppWithTelemetry = () => {
  const config = useTelemetryConfig();
  return (
    <TelemetryProvider config={config} ErrorComponent={ErrorPage}>
      <AppDependenciesProvider>
        <AppRoutes />
        <SnackbarMessage />
      </AppDependenciesProvider>
    </TelemetryProvider>
  );
};

const root = ReactDOM.createRoot(rootElement);
root.render(
  <BrowserRouter basename={baseUrl}>
    <ScrollToTop />
    <AppWithTelemetry />
  </BrowserRouter>
);
